import { defineNuxtPlugin } from 'nuxt/app'

// 简单的爬虫检测正则（避免依赖外部模块）
const SPIDER_PATTERNS = [
  /baiduspider|百度/i,
  /googlebot|google/i,
  /bingbot|msnbot/i,
  /360spider|haosou/i,
  /sogou/i,
  /yahoo.*slurp/i,
  /yandexbot/i,
  /duckduckbot/i,
  /facebookexternalhit/i,
  /twitterbot/i,
  /linkedinbot/i,
  /telegrambot/i,
  /whatsapp/i,
  /bot|spider|crawler|crawl|scraper|scrape/i
]

/**
 * 全局异常捕获插件
 * 捕获所有类型的异常，包括：
 * 1. Vue 组件错误
 * 2. 全局 JavaScript 错误
 * 3. 未捕获的 Promise 错误
 * 4. 资源加载错误
 * 
 * 特性：
 * - 爬虫过滤：自动过滤爬虫的错误上报
 * - 错误去重：避免重复上报相同错误
 * - 防止循环：避免错误处理中的递归调用
 */
export default defineNuxtPlugin((nuxtApp) => {
  // 只在客户端执行
  if (process.server) {
    return
  }

  // 保存原始的 console.error，避免循环调用
  const originalConsoleError = console.error.bind(console)
  
  // 错误去重：使用 Set 存储已处理的错误签名
  const errorSignatures = new Set<string>()
  const MAX_ERROR_SIGNATURES = 100 // 最多记录100个不同的错误签名
  
  // 资源加载错误去重（专门用于资源加载）
  const resourceErrorUrls = new Set<string>()
  const MAX_RESOURCE_ERRORS = 50 // 最多记录50个不同的资源错误
  
  // 生成错误签名（用于去重）
  const getErrorSignature = (error: any, context?: string): string => {
    const message = error?.message || String(error) || ''
    const stack = error?.stack || ''
    // 使用消息和堆栈的前100个字符作为签名
    const signature = `${context || ''}:${message.substring(0, 100)}:${stack.substring(0, 100)}`
    return signature
  }

  // 检查是否为爬虫
  const isSpider = (): boolean => {
    try {
      const userAgent = navigator.userAgent || ''
      // 使用简单的正则检测
      return SPIDER_PATTERNS.some(pattern => pattern.test(userAgent))
    } catch (e) {
      // 检测失败时默认不是爬虫
      return false
    }
  }

  // 错误信息收集函数
  const collectErrorInfo = (error: any, context?: string) => {
    // 检查是否为爬虫，爬虫的错误不上报
    if (isSpider()) {
      // 爬虫错误只记录到控制台，不上报
      originalConsoleError(`[全局异常捕获-爬虫过滤${context ? `-${context}` : ''}]`, {
        message: error?.message || String(error),
        userAgent: navigator.userAgent
      })
      return null
    }

    // 生成错误签名，用于去重
    const signature = getErrorSignature(error, context)
    
    // 检查是否已经处理过这个错误
    if (errorSignatures.has(signature)) {
      // 重复错误只记录，不上报
      return null
    }

    // 添加到已处理列表
    if (errorSignatures.size >= MAX_ERROR_SIGNATURES) {
      // 如果超过限制，清空一半（FIFO策略）
      const entries = Array.from(errorSignatures)
      errorSignatures.clear()
      entries.slice(entries.length / 2).forEach(sig => errorSignatures.add(sig))
    }
    errorSignatures.add(signature)

    const errorInfo = {
      message: error?.message || String(error),
      stack: error?.stack || '',
      name: error?.name || 'Error',
      context: context || '',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    }

    // 使用原始 console.error 输出，避免循环
    originalConsoleError(`[全局异常捕获${context ? `-${context}` : ''}]`, errorInfo)

    // 上报到 fundebug（静默处理，不抛异常）
    if (typeof window !== 'undefined' && 'fundebug' in window) {
      try {
        const fundebug = (window as any).fundebug
        if (fundebug && typeof fundebug.notifyError === 'function') {
          // 如果是 Error 对象，直接上报
          if (error instanceof Error) {
            fundebug.notifyError(error, {
              metaData: {
                context,
                url: window.location.href,
                userAgent: navigator.userAgent,
                platform: 'pc'
              }
            })
          } else {
            // 否则创建一个新的 Error 对象
            const errorObj = new Error(errorInfo.message)
            errorObj.stack = errorInfo.stack
            fundebug.notifyError(errorObj, {
              metaData: {
                context,
                originalError: String(error),
                url: window.location.href,
                userAgent: navigator.userAgent,
                platform: 'pc'
              }
            })
          }
        }
      } catch (e) {
        // 静默处理，不再抛出异常，避免循环
        // 只记录一次，避免重复日志
        if (!errorSignatures.has(`fundebug-error:${signature}`)) {
          originalConsoleError('[全局异常捕获] Fundebug 上报失败（已静默处理）')
          errorSignatures.add(`fundebug-error:${signature}`)
        }
      }
    }

    return errorInfo
  }

  // 1. Vue 错误处理器
  nuxtApp.vueApp.config.errorHandler = (err: any, instance, info) => {
    // 收集错误信息（包含爬虫过滤和去重）
    const errorInfo = collectErrorInfo(err, `Vue-${info || 'unknown'}`)
    
    // 如果错误被过滤（爬虫或重复），直接返回
    if (!errorInfo) {
      return
    }
    
    // 记录组件信息（使用原始 console.error）
    if (instance) {
      try {
        const componentName = 
          instance.$?.type?.__name || 
          instance.$?.type?.name || 
          instance.$?.type?.__file ||
          instance.$options?.name ||
          'Unknown Component'
        
        originalConsoleError('[Vue 组件错误]', {
          component: componentName,
          info: info,
          error: errorInfo
        })
      } catch (e) {
        // 静默处理
      }
    }
  }

  // 2. 全局 JavaScript 错误捕获
  const originalOnError = window.onerror
  window.onerror = (message, source, lineno, colno, error) => {
    // 调用原始的错误处理器（如果存在）
    if (originalOnError) {
      try {
        originalOnError.call(window, message, source, lineno, colno, error)
      } catch (e) {
        // 静默处理
      }
    }

    const errorObj = error || new Error(String(message))
    errorObj.stack = errorObj.stack || `at ${source}:${lineno}:${colno}`
    
    collectErrorInfo(errorObj, 'window.onerror')

    // 返回 false 表示不阻止默认错误处理
    return false
  }

  // 3. 未捕获的 Promise 错误
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason
    collectErrorInfo(error, 'unhandledrejection')

    // 如果是网络错误，可以在这里处理
    if (error?.message?.includes('fetch') || error?.message?.includes('network')) {
      // 网络错误通常不需要特殊处理
    }
  })

  // 4. 资源加载错误（图片、脚本、样式等）
  // 注意：资源加载错误通常很多，且很多是爬虫导致的，这里只记录不上报
  window.addEventListener('error', (event) => {
    // 过滤掉已经通过 window.onerror 处理的脚本错误
    if (event.error) {
      return // 脚本错误已经在 window.onerror 中处理
    }

    const target = event.target as HTMLElement
    if (target && (target.tagName === 'IMG' || target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
      // 获取资源 URL
      const resourceUrl = (target as HTMLImageElement).src || (target as HTMLLinkElement).href || ''
      
      // 如果已经记录过这个资源错误，直接跳过
      if (resourceErrorUrls.has(resourceUrl)) {
        // 阻止事件继续传播，避免被重复处理
        event.stopPropagation()
        event.preventDefault()
        return
      }
      
      // 添加到已记录列表
      if (resourceErrorUrls.size >= MAX_RESOURCE_ERRORS) {
        // 如果超过限制，清空旧的记录
        resourceErrorUrls.clear()
      }
      resourceErrorUrls.add(resourceUrl)
      
      // 资源加载错误只在开发模式下记录一次，不上报（避免大量重复上报）
      if (import.meta.dev) {
        originalConsoleError('[资源加载错误]', {
          tag: target.tagName,
          src: resourceUrl
        })
      }
      
      // 阻止事件继续传播
      event.stopPropagation()
      event.preventDefault()
    }
  }, true)

  // 5. Nuxt 错误处理钩子
  try {
    nuxtApp.hook('app:error', (err) => {
      collectErrorInfo(err, 'nuxt-app-error')
    })
  } catch (e) {
    // 如果 hook 不存在，忽略
  }

  // 6. 控制台错误增强（捕获 console.error）
  // 注意：这里要小心避免循环调用
  console.error = (...args: any[]) => {
    // 先调用原始的 console.error
    originalConsoleError.apply(console, args)
    
    // 检查是否是真正的错误对象
    const errorArg = args.find(arg => arg instanceof Error)
    if (errorArg) {
      // 收集错误信息（包含去重和爬虫过滤）
      collectErrorInfo(errorArg, 'console.error')
    }
  }
})